import { ApiClientProvider, createQueryClient, useApiClient, type ApiClient } from "@positive/api-client";
import {
  DiscardGuardProvider,
  FormatProvider,
  ListViewStoreContext,
  ShortcutProvider,
  createLocalListViewStore,
  createServerListViewStore,
  defaultFormatPreferences,
} from "@positive/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "@ui5/webcomponents-react";
import type { i18n } from "i18next";
import { useMemo, useState, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";

import type { AuthService } from "./identity/authService";
import type { HostMode } from "./identity/host";
import { AccountPreferences } from "./identity/AccountPreferences";
import { IdentityProvider, useIdentity } from "./identity/IdentityProvider";
import { SessionGate } from "./identity/SessionGate";
import { platformPermissions } from "../modules/platform/permissions";
import { PreferencesProvider, usePreferences } from "./PreferencesContext";
import type { Preferences } from "./preferences";
import type { AppRouter } from "./router";
import { setBusinessTimeZone } from "../modules/shared/businessDate";

export interface AppProps {
  i18n: i18n;
  apiClient: ApiClient;
  router: AppRouter;
  initialPreferences: Preferences;
  auth: AuthService;
  mode: HostMode;
  supportSessionId: string | null;
}

/** Formatting from the session (A5 digits, branch time zone) and the user's own choices (decision I6). */
function SessionFormatProvider({ children }: { children: ReactNode }) {
  const { session } = useIdentity();
  const { digits, hijri } = usePreferences();
  const branch = session?.branches.find((item) => item.id === session.session.branchId);
  const timeZone = branch?.effectiveTimeZone ?? session?.tenant.timeZone;
  // New documents default to the business date of this zone (SRV-L-02); set while rendering, before the pages read it.
  setBusinessTimeZone(timeZone);
  const preferences = useMemo(
    () => ({
      ...defaultFormatPreferences,
      digits: digits ?? session?.locale.digits ?? defaultFormatPreferences.digits,
      showHijri: hijri ?? session?.locale.hijriDisplay ?? defaultFormatPreferences.showHijri,
      ...(timeZone ? { timeZone } : {}),
    }),
    [digits, hijri, session?.locale.digits, session?.locale.hijriDisplay, timeZone],
  );
  return <FormatProvider preferences={preferences}>{children}</FormatProvider>;
}

/**
 * Saved table views (UX8): per user in the Platform module in the tenant app; the operator console keeps them in the
 * browser (it has no tenant user).
 */
function ListViewsProvider({ mode, children }: { mode: HostMode; children: ReactNode }) {
  const client = useApiClient();
  const { access } = useIdentity();
  const canShare = access.can(platformPermissions.listViewsManage);
  const store = useMemo(
    () => (mode.kind === "tenant" ? createServerListViewStore(client, canShare) : createLocalListViewStore()),
    [canShare, client, mode.kind],
  );
  return <ListViewStoreContext.Provider value={store}>{children}</ListViewStoreContext.Provider>;
}

/** Router with the user's access in its context (route guards, navigation filtering). */
function AppRouterProvider({ router, mode }: { router: AppRouter; mode: HostMode }) {
  const { access } = useIdentity();
  return (
    <ShortcutProvider isAllowed={access.can}>
      <DiscardGuardProvider>
        <RouterProvider router={router} context={{ access, mode }} />
      </DiscardGuardProvider>
    </ShortcutProvider>
  );
}

export function App({ i18n, apiClient, router, initialPreferences, auth, mode, supportSessionId }: AppProps) {
  const [queryClient] = useState(() => createQueryClient());

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ApiClientProvider client={apiClient}>
            <PreferencesProvider i18n={i18n} initial={initialPreferences}>
              <IdentityProvider auth={auth} mode={mode} supportSessionId={supportSessionId}>
                <AccountPreferences />
                <SessionGate>
                  <SessionFormatProvider>
                    <ListViewsProvider mode={mode}>
                      <AppRouterProvider router={router} mode={mode} />
                    </ListViewsProvider>
                  </SessionFormatProvider>
                </SessionGate>
              </IdentityProvider>
            </PreferencesProvider>
          </ApiClientProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}
